const STORAGE_KEY = 'captureMode';

const runtime = {
    enabled: false,
    pathname: '',
};

export const CAPTURE_AVATAR_SRC = `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">'
    + '<rect width="64" height="64" fill="#8b9098"/>'
    + '<circle cx="32" cy="24" r="10" fill="#f5f6f8"/>'
    + '<ellipse cx="32" cy="54" rx="16" ry="12" fill="#f5f6f8"/>'
    + '</svg>',
)}`;

export function readCaptureModePreference() {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

export function persistCaptureModePreference(enabled) {
    try {
        localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    } catch {
        // ignore private mode / blocked storage
    }
}

/** Called during render so display helpers see the current page and toggle. */
export function bindCaptureRuntime(enabled, pathname) {
    runtime.enabled = enabled === true;
    runtime.pathname = String(pathname || '');
}

export function isCaptureModeEnabled() {
    return runtime.enabled;
}

function isDashboardPath(pathname) {
    return pathname === '/admin' || pathname === '/admin/';
}

function isFinancePath(pathname) {
    return /^\/admin\/(transactions|create-transaction|edit-transaction|operational-costs|register-operational-cost|edit-operational-cost|settlement-import)(\/|$)/.test(pathname);
}

export function shouldMaskMoney() {
    return runtime.enabled && (isDashboardPath(runtime.pathname) || isFinancePath(runtime.pathname));
}

export function shouldMaskAuthCode() {
    return runtime.enabled && isFinancePath(runtime.pathname);
}

function maskMiddle(value, keepStart, keepEnd) {
    const text = String(value ?? '');
    if (!text) {
        return text;
    }
    if (text.length <= keepStart + keepEnd) {
        if (text.length <= 2) {
            return '*'.repeat(text.length);
        }
        return `${text.slice(0, 1)}${'*'.repeat(text.length - 2)}${text.slice(-1)}`;
    }
    const hidden = text.length - keepStart - keepEnd;
    return `${text.slice(0, keepStart)}${'*'.repeat(hidden)}${text.slice(-keepEnd)}`;
}

/** Fulano da Silva → Ful**********va */
export function maskName(value) {
    return maskMiddle(String(value || '').trim(), 3, 2);
}

/** email@domain.com → em**@****.com */
export function maskEmail(value) {
    const raw = String(value || '').trim();
    if (!raw) {
        return raw;
    }
    const at = raw.indexOf('@');
    if (at < 1) {
        return maskMiddle(raw, 2, 2);
    }
    const local = raw.slice(0, at);
    const domain = raw.slice(at + 1);
    const dot = domain.lastIndexOf('.');
    const tld = dot > 0 ? domain.slice(dot) : '';
    const localHead = local.slice(0, Math.min(2, local.length));
    return `${localHead}**@****${tld}`;
}

/** (41) 99999-9999 → (41) 9****-**99 */
export function maskPhone(formatted) {
    const text = String(formatted || '').trim();
    if (!text) {
        return text;
    }
    const match = text.match(/^\((\d{2})\)\s*(\d)(\d*)-(\d+)$/);
    if (!match) {
        return maskMiddle(text, 2, 2);
    }
    const [, area, first, middle, tail] = match;
    const tailKeep = tail.slice(-2);
    const tailMask = '*'.repeat(Math.max(0, tail.length - 2));
    return `(${area}) ${first}${'*'.repeat(middle.length)}-${tailMask}${tailKeep}`;
}

/** R$ 999,99 → R$ ****,99 */
export function maskMoney(formatted) {
    const text = String(formatted || '');
    return text.replace(
        /(R\$\s*)-?([\d.]+)(,(\d{2}))?/,
        (_, prefix, _integer, _comma, cents) => `${prefix}****${cents != null ? `,${cents}` : ''}`,
    );
}

export function maskEdges(value) {
    return maskMiddle(String(value || '').trim(), 2, 2);
}

export function applyName(value) {
    if (!runtime.enabled || value == null || value === '') {
        return value;
    }
    return maskName(value);
}

export function applyEmail(value) {
    if (!runtime.enabled || value == null || value === '') {
        return value;
    }
    return maskEmail(value);
}

export function applyPhone(formatted) {
    if (!runtime.enabled || formatted == null || formatted === '') {
        return formatted;
    }
    return maskPhone(formatted);
}

export function applyMoney(formatted) {
    if (!shouldMaskMoney() || formatted == null || formatted === '') {
        return formatted;
    }
    return maskMoney(formatted);
}

export function applyEdges(value) {
    if (!runtime.enabled || value == null || value === '') {
        return value;
    }
    return maskEdges(value);
}

export function applyAuthCode(value) {
    if (!shouldMaskAuthCode() || value == null || value === '') {
        return value;
    }
    return maskEdges(value);
}
